import { useMutation } from '@apollo/client/react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { useMemo } from 'react';
import { toast } from 'sonner';

import AppendixCard from './AppendixCard';
import { UPDATE_APPENDIX } from './constants';
import type { Appendix, GetLayoutTypes } from './types';

interface AppendixListProps {
  appendices: Appendix[];
  data: GetLayoutTypes;
  onSuccess: () => void;
}

const AppendixList = ({ appendices, data, onSuccess }: AppendixListProps) => {
  const sorted = useMemo(
    () => [...appendices].sort((a, b) => a.sequence - b.sequence),
    [appendices]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const [updateSequence] = useMutation(UPDATE_APPENDIX, {
    onError: () => {
      toast.error('Failed to reorder appendix');
    }
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sorted.findIndex(a => a.id === active.id);
    const newIndex = sorted.findIndex(a => a.id === over.id);
    const reordered = arrayMove(sorted, oldIndex, newIndex);

    // Update sequence values for all affected items
    const updates = reordered.map((item, index) => {
      const newSequence = index + 1;
      if (item.sequence !== newSequence) {
        return updateSequence({
          variables: { id: item.id, sequence: newSequence }
        });
      }
      return null;
    });

    void Promise.all(updates.filter(Boolean)).then(() => {
      onSuccess();
    });
  };

  const itemIds = sorted.map(a => a.id);

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <SortableContext
        items={itemIds}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {sorted.map(appendix => (
            <AppendixCard
              appendix={appendix}
              data={data}
              key={appendix.id}
              onSuccess={onSuccess}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default AppendixList;
